import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// Avatar component not available, using simple div approach
import { Search, Filter, Calendar, AlertCircle, Trash2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { StatusDropdown } from './StatusDropdown';
import { TaskDetailDialog } from './TaskDetailDialog';
import taskService, { TaskApiResponse } from '@/services/task.service';
import { toast } from 'sonner';
import { baseURL } from '@/lib/axios';
import { getStatusesByCategory } from '@/services/status.service';
import { useAuthStore } from '@/stores/authStore';

interface Project {
  _id?: string;
  id: string;
  name: string;
  client: string;
  status: string;
  priority: string;
  progress: number;
  description: string;
  startDate: string;
  endDate: string;
  createdDate: string;
  projectManager: string;
  team: string[];
  tags: string[];
  totalLoggedHours: number;
  hoursByDay: Array<{ day: string; hours: number }>;
  tasks: {
    total: number;
    notStarted: number;
    inProgress: number;
    testing: number;
    awaitingFeedback: number;
    readyForProduction: number;
    onHold: number;
    complete: number;
  };
}

interface Task {
  id: string;
  name: string;
  status: string;
  statusId?: string; // MongoDB ID of the status for API updates
  startDate: string;
  dueDate: string;
  assignedTo: {
    name: string;
    avatar?: string;
    initials: string;
  };
  assignees?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
    profilePicture?: string | null;
    initials?: string;
  }>;
  followers?: Array<{
    _id: string;
    firstName: string;
    lastName: string;
    email: string;
  }>;
  tags: string[];
  priority: string;
  estimatedHours: number;
  description?: string;
  url?: string;
  isPublic?: boolean;
  isBillable?: boolean;
  isVisibleToCustomer?: boolean;
}

interface TasksTabProps {
  project: Project;
  refreshTrigger?: number; // Add a prop to trigger refresh from parent
  initialOpenTaskId?: string;
}

// Helper function to generate initials from name
const getInitials = (firstName?: string, lastName?: string, email?: string): string => {
  if (!firstName && !lastName) {
    if (email) {
      return email.slice(0, 2).toUpperCase();
    }
    return 'UN'; // Unassigned
  }
  
  const first = firstName?.charAt(0) || '';
  const last = lastName?.charAt(0) || '';
  
  if (first && last) {
    return `${first}${last}`.toUpperCase();
  }
  
  // If only one name is available, use first two letters
  const name = firstName || lastName || '';
  return name.slice(0, 2).toUpperCase() || 'UN';
};

const apiBaseWithoutPrefix = baseURL ? baseURL.replace(/\/api\/v1\/?$/, '') : '';

const buildImageUrlIfNeeded = (path?: string | null) => {
  if (!path) return null;
  if (path.startsWith('http')) {
    return path;
  }
  if (!apiBaseWithoutPrefix) {
    return path;
  }
  const normalizedBase = apiBaseWithoutPrefix.replace(/\/$/, '');
  const normalizedPath = path.startsWith('/') ? path.substring(1) : path;
  return `${normalizedBase}/${normalizedPath}`;
};

// Helper function to convert API response to UI Task format
const convertApiTaskToUITask = (apiTask: TaskApiResponse): Task => {
  const firstAssignee = apiTask.assignees && apiTask.assignees.length > 0 
    ? apiTask.assignees[0] 
    : null;
  
  // Safely get assignee name
  const getAssigneeName = () => {
    if (!firstAssignee) return 'Unassigned';
    
    const firstName = firstAssignee.firstName || '';
    const lastName = firstAssignee.lastName || '';
    const email = firstAssignee.email || '';
    
    if (firstName || lastName) {
      return `${firstName} ${lastName}`.trim();
    }
    
    if (email) {
      return email; // Fallback to email if no name
    }
    
    return 'Unassigned';
  };
  
  // Extract status from API response - can be string (ID), object (populated), or undefined
  const getStatus = () => {
    if (!apiTask.status) return null;
    
    // If status is an object (populated), use the name
    if (typeof apiTask.status === 'object' && 'name' in apiTask.status) {
      return {
        id: apiTask.status._id,
        name: apiTask.status.name,
      };
    }
    
    // If status is a string, it could be an ID or a name
    return {
      id: apiTask.status,
      name: apiTask.status, // Will be matched by StatusDropdown
    };
  };

  const statusInfo = getStatus();

  return {
    id: apiTask._id,
    name: apiTask.title,
    status: statusInfo?.name || statusInfo?.id || 'NOT_STARTED',
    statusId: statusInfo?.id, // Store the status ID separately for API updates
    startDate: apiTask.startDate,
    dueDate: apiTask.dueDate || '',
    assignedTo: {
      name: getAssigneeName(),
      initials: getInitials(firstAssignee?.firstName, firstAssignee?.lastName, firstAssignee?.email),
      avatar: buildImageUrlIfNeeded(firstAssignee?.profilePicture) ?? undefined,
    },
    assignees: (apiTask.assignees || []).map((assignee) => ({
      ...assignee,
      initials: getInitials(assignee.firstName, assignee.lastName, assignee.email),
    })), // Pass through all assignees with initials
    followers: apiTask.followers, // Pass through all followers
    tags: (apiTask.tags || []).map((tag: any) => 
      typeof tag === 'string' ? tag : (tag?.name || tag?.title || String(tag))
    ),
    priority: apiTask.priority,
    estimatedHours: apiTask.estimateHours,
    description: apiTask.description,
    url: apiTask.url,
  };
};


const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'High':
      return 'bg-red-100 text-red-800 border-red-200';
    case 'Medium':
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'Low':
      return 'bg-green-100 text-green-800 border-green-200';
    default:
      return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

// Helper function to normalize status for comparison (handles case, spaces, underscores)
const normalizeStatusForComparison = (status: string): string => {
  if (!status) return '';
  return String(status)
    .toUpperCase()
    .trim()
    .replace(/\s+/g, '_') // Replace spaces with underscores
    .replace(/-/g, '_'); // Replace hyphens with underscores
};

// Helper function to check if a task status matches a target status (case-insensitive, handles spaces/underscores)
const statusMatches = (taskStatus: string, targetStatus: string): boolean => {
  return normalizeStatusForComparison(taskStatus) === normalizeStatusForComparison(targetStatus);
};

export default function TasksTab({ project, refreshTrigger, initialOpenTaskId }: TasksTabProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isTaskDetailOpen, setIsTaskDetailOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const itemsPerPage = 10;
  const [taskStatuses, setTaskStatuses] = useState<Array<{ name: string; id: string; isActive: boolean }>>([]);
  const [taskToDelete, setTaskToDelete] = useState<Task | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const { hasSubModulePermission } = useAuthStore();

  // Fetch task statuses from API for filter dropdown
  useEffect(() => {
    const fetchTaskStatuses = async () => {
      try {
        const statuses = await getStatusesByCategory('task');
        setTaskStatuses(statuses.map(s => ({ name: s.name, id: s.id, isActive: s.isActive })));
      } catch (error) {
        console.error('Error fetching task statuses:', error);
      }
    };
    fetchTaskStatuses();
  }, []);

  // Fetch tasks from API
  useEffect(() => {
    const fetchTasks = async () => {
      const projectId = project._id || project.id;
      
      if (!projectId) {
        console.error('No project ID available. Project:', project);
        toast.error('Project ID not found');
        return;
      }
      
      console.log('Fetching tasks for project ID:', projectId);
      setIsLoading(true);
      try {
        const apiTasks = await taskService.getTasksByProject(projectId);
        console.log('Fetched tasks:', apiTasks);
        
        if (!apiTasks) {
          console.warn('API returned null/undefined tasks');
          setTasks([]);
          return;
        }
        
        // Handle if API returns object with data property
        const tasksArray = Array.isArray(apiTasks) ? apiTasks : ((apiTasks as any)?.data || []);
        console.log('Tasks array:', tasksArray);
        
        const uiTasks = tasksArray.map(convertApiTaskToUITask);
        console.log('Converted UI tasks:', uiTasks);
        setTasks(uiTasks);
      } catch (error: any) {
        console.error('Error fetching tasks:', error);
        const errorMessage = error.response?.data?.message || error.message || 'Failed to load tasks';
        toast.error(errorMessage);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTasks();
  }, [project._id, project.id, refreshTrigger]);

  // Open a task detail if requested by parent (e.g., via notification click)
  useEffect(() => {
    const openTaskId = (location.state as any)?.openTaskId || initialOpenTaskId;
    if (!openTaskId) return;
    
    // Try to find the task in the current list
    const match = tasks.find(t => t.id === openTaskId);
    
    if (match) {
      // Task found in list, open it directly
      setSelectedTask(match);
      setIsTaskDetailOpen(true);
      // Clear the navigation state so a page refresh doesn't auto-open again
      try {
        navigate(location.pathname, { replace: true, state: {} });
      } catch {}
    } else if (tasks.length > 0) {
      // Tasks are loaded but this task is not in the list - fetch it directly
      // This can happen if the task was filtered out or moved
      const fetchAndOpenTask = async () => {
        try {
          console.log('TasksTab: Task not in list, fetching directly:', openTaskId);
          const taskApi = await taskService.getTaskById(openTaskId);
          const task = convertApiTaskToUITask(taskApi);
          setSelectedTask(task);
          setIsTaskDetailOpen(true);
          // Clear the navigation state
          try {
            navigate(location.pathname, { replace: true, state: {} });
          } catch {}
        } catch (error) {
          console.error('TasksTab: Failed to fetch task for opening:', error);
        }
      };
      fetchAndOpenTask();
    }
    // If tasks.length === 0, we'll wait for tasks to load and this effect will run again
  }, [initialOpenTaskId, tasks, location.state, location.pathname, navigate]);

  const filteredTasks = tasks.filter((task, index) => {
    // Calculate sequential number for search
    const sequentialNumber = String(index + 1).padStart(2, '0');
    
    const matchesSearch = task.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         sequentialNumber.includes(searchTerm) ||
                         task.assignedTo.name.toLowerCase().includes(searchTerm.toLowerCase());
    
    // Normalize status comparison - handle case-insensitive matching
    const matchesStatus = statusFilter === 'all' || 
      (task.status && String(task.status).toUpperCase().trim() === statusFilter.toUpperCase().trim());
    
    const matchesPriority = priorityFilter === 'all' || task.priority === priorityFilter;
    
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleStatusChange = async (taskId: string, statusId: string, statusName: string) => {
    try {
      console.log(`Updating task ${taskId} status to ID: ${statusId}, Name: ${statusName}`);
      
      // Optimistically update UI with status name and ID for display
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, status: statusName, statusId: statusId } : task
        )
      );

      // Call API to update status - API expects MongoDB ID
      await taskService.updateTaskStatus(taskId, statusId);
      
      toast.success('Task status updated successfully');
    } catch (error: any) {
      console.error('Error updating task status:', error);
      
      // Revert optimistic update on error
      setTasks(prevTasks =>
        prevTasks.map(task =>
          task.id === taskId ? { ...task, status: task.status, statusId: task.statusId } : task
        )
      );
      
      const errorMessage = error.response?.data?.message || 'Failed to update task status';
      toast.error(errorMessage);
    }
  };

  const handleTaskClick = (task: Task) => {
    setSelectedTask(task);
    setIsTaskDetailOpen(true);
  };

  const handleTaskDetailClose = (open: boolean) => {
    setIsTaskDetailOpen(open);
    if (!open) {
      setSelectedTask(null);
    }
  };

  const handleDeleteClick = (task: Task, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click
    setTaskToDelete(task);
    setIsDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!taskToDelete) return;

    try {
      await taskService.deleteTask(taskToDelete.id);
      toast.success('Task deleted successfully');
      
      // Remove task from local state
      setTasks(prevTasks => prevTasks.filter(task => task.id !== taskToDelete.id));
      
      // Close task detail if it was the deleted task
      if (selectedTask && selectedTask.id === taskToDelete.id) {
        setIsTaskDetailOpen(false);
        setSelectedTask(null);
      }
      
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    } catch (error: any) {
      console.error('Error deleting task:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete task';
      toast.error(errorMessage);
      setIsDeleteDialogOpen(false);
      setTaskToDelete(null);
    }
  };

  const totalPages = Math.ceil(filteredTasks.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedTasks = filteredTasks.slice(startIndex, startIndex + itemsPerPage);

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isOverdue = (dueDate: string) => {
    return new Date(dueDate) < new Date() && statusFilter !== 'Complete';
  };

  return (
    <div className="space-y-6">
      {/* Tasks Overview */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Calendar className="h-5 w-5" />
            <span>Tasks Overview</span>
          </CardTitle>
          <CardDescription>Current snapshot of task distribution</CardDescription>
        </CardHeader>
        <CardContent>
          {(() => {
            // Calculate dynamic counts from actual task data
            // Use case-insensitive matching that handles spaces, underscores, and hyphens
            const counts = {
              total: tasks.length,
              notStarted: tasks.filter(t => statusMatches(t.status, 'NOT_STARTED') || statusMatches(t.status, 'NOT STARTED')).length,
              inProgress: tasks.filter(t => statusMatches(t.status, 'IN_PROGRESS') || statusMatches(t.status, 'IN PROGRESS')).length,
              testing: tasks.filter(t => statusMatches(t.status, 'TESTING')).length,
              awaitingFeedback: tasks.filter(t => statusMatches(t.status, 'AWAITING_FEEDBACK') || statusMatches(t.status, 'AWAITING FEEDBACK')).length,
              readyForProduction: tasks.filter(t => statusMatches(t.status, 'READY_FOR_PRODUCTION') || statusMatches(t.status, 'READY FOR PRODUCTION')).length,
              onHold: tasks.filter(t => statusMatches(t.status, 'ON_HOLD') || statusMatches(t.status, 'ON HOLD')).length,
              complete: tasks.filter(t => statusMatches(t.status, 'COMPLETE') || statusMatches(t.status, 'COMPLETED')).length,
            };
            const total = counts.total;
            const pct = (count: number) => (total > 0 ? Math.round((count / total) * 100) : 0);
            const items = [
              {
                label: 'Total',
                value: counts.total,
                color: 'from-gray-100 to-gray-50',
                bar: 'bg-gray-400',
                text: 'text-gray-900',
                percent: 100,
              },
              {
                label: 'Not Started',
                value: counts.notStarted,
                color: 'from-gray-50 to-white',
                bar: 'bg-gray-500',
                text: 'text-gray-700',
                percent: pct(counts.notStarted),
              },
              {
                label: 'In Progress',
                value: counts.inProgress,
                color: 'from-blue-50 to-white',
                bar: 'bg-blue-500',
                text: 'text-blue-700',
                percent: pct(counts.inProgress),
              },
              {
                label: 'Testing',
                value: counts.testing,
                color: 'from-purple-50 to-white',
                bar: 'bg-purple-500',
                text: 'text-purple-700',
                percent: pct(counts.testing),
              },
              {
                label: 'Awaiting Feedback',
                value: counts.awaitingFeedback,
                color: 'from-yellow-50 to-white',
                bar: 'bg-yellow-500',
                text: 'text-yellow-700',
                percent: pct(counts.awaitingFeedback),
              },
              {
                label: 'Ready',
                value: counts.readyForProduction,
                color: 'from-green-50 to-white',
                bar: 'bg-green-500',
                text: 'text-green-700',
                percent: pct(counts.readyForProduction),
              },
              {
                label: 'On Hold',
                value: counts.onHold,
                color: 'from-red-50 to-white',
                bar: 'bg-red-500',
                text: 'text-red-700',
                percent: pct(counts.onHold),
              },
              {
                label: 'Complete',
                value: counts.complete,
                color: 'from-emerald-50 to-white',
                bar: 'bg-emerald-500',
                text: 'text-emerald-700',
                percent: pct(counts.complete),
              },
            ];

            return (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {items.map((it) => (
                  <div
                    key={it.label}
                    className={cn(
                      'rounded-lg border p-3 bg-gradient-to-br',
                      it.color
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className={cn('text-xs font-medium', it.text)}>{it.label}</span>
                      <span className="text-xs text-muted-foreground">{it.percent}%</span>
                    </div>
                    <div className="mt-1.5 flex items-end justify-between">
                      <div className={cn('text-xl font-bold', it.text)}>{it.value}</div>
                    </div>
                    <div className="mt-2 h-1.5 w-full rounded-full bg-gray-200">
                      <div
                        className={cn('h-1.5 rounded-full', it.bar)}
                        style={{ width: `${Math.min(it.percent, 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {/* Filters and Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Filter className="h-5 w-5" />
            <span>Filter & Search Tasks</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search tasks..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {taskStatuses
                  .filter(status => status.isActive)
                  .map((status) => (
                    <SelectItem key={status.id} value={status.name.toUpperCase()}>
                      {status.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <Select value={priorityFilter} onValueChange={setPriorityFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Filter by priority" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Priorities</SelectItem>
                <SelectItem value="High">High</SelectItem>
                <SelectItem value="Medium">Medium</SelectItem>
                <SelectItem value="Low">Low</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant="outline"
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setPriorityFilter('all');
              }}
            >
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Tasks Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tasks ({filteredTasks.length})</CardTitle>
          <CardDescription>
            Detailed view of all project tasks
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]" role="status">
                  <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">Loading...</span>
                </div>
                <p className="mt-2 text-sm text-gray-500">Loading tasks...</p>
              </div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-center">
                <p className="text-gray-500">No tasks found for this project</p>
                <p className="text-sm text-gray-400 mt-1">Create a task to get started</p>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Task ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Assigned To</TableHead>
                    <TableHead>Tags</TableHead>
                    <TableHead>Priority</TableHead>
                    <TableHead>Est. Hours</TableHead>
                    {hasSubModulePermission('project_management', 'task', 'delete') && (
                      <TableHead>Actions</TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {paginatedTasks.map((task, index) => (
                  <TableRow 
                    key={task.id}
                    className="cursor-pointer hover:bg-gray-50"
                    onClick={() => handleTaskClick(task)}
                  >
                    <TableCell className="font-medium">
                      {String(startIndex + index + 1).padStart(2, '0')}
                    </TableCell>
                    <TableCell className="max-w-xs">
                      <div className="truncate">{task.name}</div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <StatusDropdown
                        currentStatus={task.status}
                        onStatusChange={(statusId, statusName) => handleStatusChange(task.id, statusId, statusName)}
                      />
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(task.startDate)}
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <span>{formatDate(task.dueDate)}</span>
                        {isOverdue(task.dueDate) && (
                          <AlertCircle className="h-4 w-4 text-red-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {task.assignees && task.assignees.length > 0 ? (
                        <div className="flex flex-wrap items-center gap-2">
                          {task.assignees.map((assignee) => {
                            const imageUrl = assignee.profilePicture ? buildImageUrlIfNeeded(assignee.profilePicture) : null;
                            return (
                              <div
                                key={assignee._id}
                                className="relative h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800 overflow-hidden"
                                title={`${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim() || assignee.email || 'Assignee'}
                              >
                                {imageUrl ? (
                                  <img
                                    src={imageUrl}
                                    alt={`${assignee.firstName ?? ''} ${assignee.lastName ?? ''}`.trim() || assignee.email || 'Assignee'}
                                    className="h-full w-full object-cover"
                                    onError={(e) => {
                                      e.currentTarget.style.display = 'none';
                                      e.currentTarget.nextElementSibling?.classList.remove('hidden');
                                    }}
                                  />
                                ) : null}
                                <span className={`${imageUrl ? 'hidden' : ''}`}>
                                  {assignee.initials}
                                </span>
                              </div>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center text-xs font-medium text-blue-800">
                          {task.assignedTo.initials}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(task.tags as any[]).map((tag: any, tagIndex: number) => {
                          const tagName = typeof tag === 'string' ? tag : (tag?.name || tag?.title || String(tag));
                          const tagKey = typeof tag === 'string' ? tag : (tag?._id || tag?.id || tagIndex);
                          return (
                            <Badge key={tagKey} variant="secondary" className="text-xs">
                              {tagName}
                            </Badge>
                          );
                        })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={cn('px-2 py-1 text-xs', getPriorityColor(task.priority))}>
                        {task.priority}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-gray-600">
                      {task.estimatedHours}h
                    </TableCell>
                    {hasSubModulePermission('project_management', 'task', 'delete') && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={(e) => handleDeleteClick(task, e)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!isLoading && tasks.length > 0 && totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + itemsPerPage, filteredTasks.length)} of {filteredTasks.length} tasks
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <div className="flex items-center space-x-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                    <Button
                      key={page}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      className="w-8 h-8 p-0"
                    >
                      {page}
                    </Button>
                  ))}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Task Detail Dialog */}
      <TaskDetailDialog
        open={isTaskDetailOpen}
        onOpenChange={handleTaskDetailClose}
        task={selectedTask}
        onTaskUpdate={async (taskId, updates) => {
          console.log('Updating task in TasksTab:', taskId, updates);
          
          try {
            // Refetch the task to get complete data including profile pictures
            const updatedTaskApi = await taskService.getTaskById(taskId);
            const updatedTask = convertApiTaskToUITask(updatedTaskApi);
            
            // Update the task list with the refetched task data
            setTasks(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? updatedTask : task
              )
            );
            
            // Update selectedTask if it's the one being updated
            if (selectedTask && selectedTask.id === taskId) {
              setSelectedTask(updatedTask);
            }
          } catch (error) {
            console.error('Error refetching task after update:', error);
            // Fallback to the original update logic if refetch fails
            setTasks(prevTasks =>
              prevTasks.map(task =>
                task.id === taskId ? { ...task, ...updates } : task
              )
            );
            
            if (selectedTask && selectedTask.id === taskId) {
              setSelectedTask({ ...selectedTask, ...updates });
            }
          }
        }}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Task</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete the task "{taskToDelete?.name}"?
              <br /><br />
              This action cannot be undone and will permanently remove the task.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setIsDeleteDialogOpen(false);
              setTaskToDelete(null);
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700 focus:ring-red-600"
            >
              Delete Task
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}